import React from 'react'
import Render from 'core/layout/Render'
import Link from 'next/link'
import FeaturedRecipe from './FeaturedRecipe'
import CollapsablePanel from './CollapsablePanel'
import NoSSR from 'core/helpers/nossr'
import getConfig from 'next/config'
import Grid, { Cell } from 'core/styles/Grid'
import SwiperCore, { Pagination } from 'swiper'
import styled, { css } from 'styled-components'
import { Title, Subtitle } from 'core/styles/Title'
import { Container } from 'core/styles/Container'
import { sup } from 'core/helpers/string'
import { light } from 'core/helpers/color'
import { useRouter } from 'next/router'
import { Social } from '../layout/Footer'
import { InvertedCta } from '../styles/Cta'
import { breakpoint } from '../layout/styles'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useMediaQuery } from '../helpers/mq'
import { Popover } from 'react-tiny-popover'
import ReactHtmlParser from 'react-html-parser'
import PearWidget from 'core/components/PearWidget';

SwiperCore.use([Pagination])

declare global {
  interface Window {
    yotpo: any
  }
}

const Product = ({ item: product, social, translations }: any) => {
  const router = useRouter()
  const [selectedImage, setSelectedImage] = React.useState(product?.images?.[0])
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0)
  const ref = React.useRef(null)
  const psref = React.useRef(null)
  const { publicRuntimeConfig } = getConfig()
  const background = product?.color?.[0]?.background
  const desktop = useMediaQuery(breakpoint.desktop)
  const isTablet = useMediaQuery(breakpoint.tablet)
  const isMobile = useMediaQuery(breakpoint.tablet_down)
  const images = [product.alternate_pdp_image].concat(product.images).filter((image: any) => image)
  const hasAlternateImage = product.alternate_pdp_image
  const productShotStyling = (index: any) => index < (hasAlternateImage ? 2 : 1)
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).style.opacity = '1'
  }
  const [popover, setPopover] = React.useState<string>()
  const [previewLoaded, setPreviewLoaded] = React.useState(false)
  const selectImage = (image: any, index: number) => {
    setPreviewLoaded(false)
    setSelectedImage(image)
    setSelectedImageIndex(index)
  }

  const handleBuyNow = (e: any, product: any) => {
    const { gtm_activity_string } = product

    if (!publicRuntimeConfig.productLevelGTM) return
    if (typeof (window as any).gtag !== 'function') return
    ;(window as any).gtag('event', 'conversion', {
      allow_custom_scripts: true,
      u1: '[Product]',
      send_to: `${publicRuntimeConfig.productLevelGTM}/retar0/buyno0+standard`
    })

    if (!gtm_activity_string) return
    ;(window as any).gtag('event', 'conversion', {
      allow_custom_scripts: true,
      send_to: `${publicRuntimeConfig.productLevelGTM}/buyno0/${gtm_activity_string}+standard`
    })

    if(!translations?.pixels?.dv360_pixels) return
    ;(window as any).gtag('event', 'conversion', {
      'allow_custom_scripts': true,
      'send_to': `${translations?.pixels?.dv360_pixels}/invmedia/highl0+standard`
    });
  }

  const isCDNcompatible = (item: string): boolean => {
    if (router?.locale !== 'en-CA') {
      return false;
    }
    const itemsToVerify = ['Potassium', 'Calcium', 'Iron / Fer'];

    return itemsToVerify.includes(item);
  }

 // Helper function to make "Contains" or "Contient" bold for Canadian locales
  const processBoldContains = (htmlString: string, locale: string | undefined): string => {
    // Only apply bold formatting for Canadian English and French
    if (locale === 'en-CA' || locale === 'fr-CA' || locale === 'fr') {
      // Replace "Contains" or "Contient " (with space) - case-insensitive
      return htmlString
        .replace(/\b(Contains)\b/gi, '<b>$1</b>')
        .replace(/\b(Contient)\s?/gi, '<b>Contient</b> ');
    }
    return htmlString;
  };

  React.useEffect(() => {
    // Cap at 100 attempts (10 seconds) — if Yotpo hasn't loaded by then it's
    // blocked by an ad blocker or script error. Without the cap the interval
    // runs at 100ms indefinitely for the lifetime of the page, wasting CPU.
    let attempts = 0
    const interval = setInterval(() => {
      if (++attempts > 100 || (ref.current && window.yotpo)) {
        clearInterval(interval)
        if (ref.current && window.yotpo) {
          if (!window.yotpo.initialized) {
            window.yotpo.init();
          }
          window.yotpo.refreshWidgets();
        }
      }
    }, 100)

    if (publicRuntimeConfig.productLevelGTM && typeof (window as any).gtag === 'function') {
      ;(window as any).gtag('event', 'conversion', {
        allow_custom_scripts: true,
        u1: '[Product]',
        send_to: `${publicRuntimeConfig.productLevelGTM}/retar0/produ0+standard`
      })
    }

    return () => clearInterval(interval)
  }, [])

  // Reset the selected image when the route changes (e.g. locale switch or
  // navigating to a different product). router.asPath is a stable string that
  // changes on every meaningful navigation without causing spurious re-runs —
  // unlike router.query which is a new object reference on every render in
  // Next.js 12, causing this effect to fire on every render.
  React.useEffect(() => {
    selectImage(images[0], 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  return (
    <Wrapper>
      <Container>
        <InvertedCta href={router.locale === 'en-US' ? '/products' : `/${router.locale}/products`}>{translations.product.all_products}</InvertedCta>
      </Container>
      <Container style={{ display: 'flex', flexDirection: 'column' }}>
        {publicRuntimeConfig?.productSubtitle && product.filters?.[0] && (
          <Subtitle>{ReactHtmlParser(product.filters?.[0].title)}</Subtitle>
        )}
        <Title
          dangerouslySetInnerHTML={{
            __html: sup(product.short_title || product.title)
          }}
        />
        {product.images?.length > 1 && (
          <SwiperContainer>
            <Swiper
              style={{ minHeight: 460, paddingBottom: 16 }}
              pagination={{ clickable: true }}
              slidesPerView={1}
              spaceBetween={24}
              centeredSlides={true}>
              {product.images?.map((image: any, index: any) => (
                <SwiperSlide key={image.uid}>
                  <ImageContainer background={light(background)} shot={index === 0}>
                    <img
                      src={`${image.url}?height=600&size=600&filetype=webp`}
                      srcSet={`${image.url}?height=600&size=1200&filetype=webp 2x`}
                      loading="lazy"
                      onLoad={handleImageLoad}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        opacity: 0,
                        transition: 'opacity 400ms ease'
                      }}
                    />
                  </ImageContainer>
                </SwiperSlide>
              ))}
            </Swiper>
          </SwiperContainer>
        )}

        <Grid>
          {(desktop || product.images?.length === 1) && (
            <Cell md={6}>
              <div>
                <Grid>
                  <ThumbnailContainer md={2}>
                    {images.map((image: any, index: number) => (
                      <Thumbnail
                        selected={selectedImageIndex === index}
                        background={background}
                        key={image.uid}
                        onClick={() => selectImage(image, index)}>
                        <img
                          src={`${image.url}?width=256&size=256&filetype=webp`}
                          width={productShotStyling(index) ? 70 : '100%'}
                          height={productShotStyling(index) ? 70 : '100%'}
                          loading="lazy"
                          onLoad={handleImageLoad}
                          style={{
                            display: 'block',
                            objectFit: productShotStyling(index) ? 'contain' : 'cover',
                            opacity: 0,
                            transition: 'opacity 300ms ease'
                          }}
                        />
                      </Thumbnail>
                    ))}
                  </ThumbnailContainer>
                  <Cell md={10}>
                    <Preview background={light(background)} shot={productShotStyling(selectedImageIndex)}>
                      {selectedImage && (
                        <img
                          src={`${selectedImage?.url}?width=450&size=450&filetype=webp`}
                          srcSet={`${selectedImage?.url}?width=900&size=900&filetype=webp 2x`}
                          loading="lazy"
                          onLoad={() => setPreviewLoaded(true)}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            opacity: previewLoaded ? 1 : 0,
                            transition: 'opacity 400ms ease'
                          }}
                        />
                      )}
                    </Preview>
                  </Cell>
                </Grid>
              </div>
            </Cell>
          )}
          <ProductInfo md={6} gap={1}>
            <Reviews>
              <NoSSR>
                <div className="yotpo bottomLine"
                     data-appkey={translations.api_key}
                     data-product-id={product.sku}
                     data-name={product.title}
                     data-url={router.asPath}
                     data-image-url={product.images?.[0]?.url}
                />
              </NoSSR>
            </Reviews>
            <Description>{product.description}</Description>
            {product.product_details && (
              <Description>
                <b>{translations.product_details}</b>: {product.product_details}
              </Description>
            )}
            {!publicRuntimeConfig?.disablePearCommerce && (
              <ActionsWrapper>
                {translations.pear_commerce?.vendor_id && (
                  <NoSSR>
                    <div key={`product-${product.sku}`}>
                      {isTablet && (
                        <PearWidget
                          vendorId={translations.pear_commerce?.vendor_id}
                          widget="retailer-list">
                            <div id='pear-pdp-target'/>
                        </PearWidget>
                      )}
                    </div>
                  </NoSSR>
                )}
                <Actions>
                  <div style={{ color: 'var(--text)' }}>{translations.share_this_product}</div>
                  <div>
                    <Social margin={8} mt={2} useBigSpace>
                      {social?.social_share?.map((item: any) => (
                        <a href={item.link.href} key={item._metadata.uid} target="_blank" rel="noopener noreferrer">
                          <img src={`${item.icon.url}?auto=svg`} alt={item.link.title} />
                        </a>
                      ))}
                    </Social>
                  </div>
                </Actions>
              </ActionsWrapper>
            )}
            {publicRuntimeConfig?.alternateProductDetails && (
              <Actions>
                <ProductBtnContainer>
                  <Link href="/contact" passHref>
                    <Button light={false}>{translations.product.contact_rep || 'Contact a rep'}</Button>
                  </Link>

                  {!publicRuntimeConfig?.disableDownloadPDF?.includes(router.locale) && (
                    <Button
                      target="_blank"
                      href={`https://sellsheets.syndigo.com/food/${product.uid}?GTIN=${product.gtin}`}
                      light={true}>
                      {translations.product.download_pdf || 'Download PDF'}
                    </Button>
                  )}

                  {translations.product.product_guide?.href && (
                    <Button target="_blank" href={translations.product.product_guide.href} light={true}>
                      {translations.product.product_guide.title}
                    </Button>
                  )}
                </ProductBtnContainer>
              </Actions>
            )}
            {!publicRuntimeConfig?.alternateProductDetails && (
              <InformationPanels>
                {product.information_panels?.map((ip: any) => {
                  if (ip.nutritional_information)
                    return (
                      <CollapsablePanel
                        title={translations.product?.nutritional_information}
                        key={ip.nutritional_information._metadata.uid}>
                        <Table>
                          <tbody>
                            <Row>
                              <td colSpan={3} style={{ padding: '8px 0', textAlign: 'left' }}>
                                {ip.nutritional_information.serving_size}
                              </td>
                            </Row>
                            <Row>
                              <th
                                colSpan={2}
                                style={{
                                  padding: '8px 0',
                                  textAlign: 'left',
                                  border: '1px rgba(102, 102, 102, 0.2) solid',
                                  borderLeft: 0,
                                  borderRight: 0
                                }}>
                                {translations.product.amount}
                              </th>
                              <th
                                style={{
                                  color: 'rgba(102, 102, 102, 1)',
                                  padding: '8px 0',
                                  textAlign: 'right',
                                  border: '1px rgba(102, 102, 102, 0.2) solid',
                                  borderLeft: 0,
                                  borderRight: 0
                                }}>
                                {translations.product.daily_value}
                              </th>
                            </Row>
                            {ip.nutritional_information.items.map((ni: any) => (
                              <Row sub={ni.sub} key={ni._metadata.uid}>
                                <td style={{ width: '60%' }}>{ni.sub ? <span>{ni.item}</span> : <b>{ni.item}</b>}</td>
                                <td>{ni.amount}</td>
                                <td style={{ textAlign: 'right' }}>{ni.daily_value}</td>
                              </Row>
                            ))}
                          </tbody>
                        </Table>
                      </CollapsablePanel>
                    )

                  if (ip.cooking_instructions)
                    return (
                      <CollapsablePanel
                        title={translations.product?.cooking_instructions}
                        key={ip.cooking_instructions._metadata.uid}>
                        {ip.cooking_instructions.items.map((ci: any, j: any) => (
                          <div key={`${ci.step}_${j}`}>
                            {ci.step && (
                              <div key={ci._metadata.uid}>
                                <h4>{ci.method}</h4>
                                <ul>
                                  {ci.step.map((step: any, index: any) => (
                                    <li key={`step${index}`}>{step}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {ci.value && <div dangerouslySetInnerHTML={{ __html: ci.value }} />}
                          </div>
                        ))}
                      </CollapsablePanel>
                    )

                  if (ip.mixed_content)
                    return (
                      <CollapsablePanel title={ip.mixed_content.title} key={ip.mixed_content._metadata.uid}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: ip.mixed_content.content
                          }}
                        />
                      </CollapsablePanel>
                    )

                  if (ip.icon_highlights)
                    return (
                      <div style={{ display: 'flex' }} key={ip.icon_highlights._metadata.uid}>
                        {ip.icon_highlights.items.map((item: any) => {
                            let sustainabilityPopoverText = ''

                            if (item.tooltip === 'sustainability') {
                              sustainabilityPopoverText = translations.product?.sustainability_disclaimer
                            }
                            else if (item.tooltip === 'sustainability alternative') {
                              sustainabilityPopoverText = translations.product?.sustainability_alternative_disclaimer
                            }

                            return sustainabilityPopoverText ? (
                              <Popover
                                key={item._metadata.uid}
                                isOpen={popover === item.tooltip}
                                positions={['top', 'bottom']} // preferred positions by priority
                                content={
                                  <PopoverContent
                                    dangerouslySetInnerHTML={{ __html: sustainabilityPopoverText}}
                                    onMouseEnter={() => setPopover(item.tooltip)}
                                    onMouseLeave={() => setPopover(undefined)}
                                  />
                                }>
                                <IconHighlight
                                  key={item._metadata.uid}
                                  onMouseEnter={() => setPopover(item.tooltip)}
                                  onMouseLeave={() => setPopover(undefined)}>
                                  <Info>ⓘ</Info>
                                  <Icon>{item.icon ? <img src={item.icon?.url} /> : item.icon_text}</Icon>
                                  <Text>{item.text}</Text>
                                </IconHighlight>
                              </Popover>
                            ) : (
                              <IconHighlight key={item._metadata.uid}>
                                <Icon>{item.icon ? <img src={item.icon?.url} /> : item.icon_text}</Icon>
                                <Text>{item.text}</Text>
                              </IconHighlight>
                            )

                          })
                        }
                      </div>
                    )
                })}
              </InformationPanels>
            )}
          </ProductInfo>
        </Grid>
      </Container>

      {publicRuntimeConfig?.alternateProductDetails && (
        <DetailsContainer>
          <Grid>
            <Cell md={8}>
              <Grid>
                <Cell md={6}>
                  {product.information_panels?.find((ip: any) => ip.product_specifications)?.product_specifications &&
                    (product.information_panels?.find((ip: any) => ip.product_specifications)?.product_specifications.items
                      ?.length ?? 0) > 0 && (
                      <div>
                        <h3>{translations.product?.product_specifications}</h3>
                        {product.information_panels
                          ?.find((ip: any) => ip.product_specifications)
                          ?.product_specifications?.items.map((ci: any) => (
                            <div style={{ marginBottom: 8 }} key={ci.key}>
                              {ci.step && (
                                <div key={ci._metadata.uid}>
                                  <h4>{ci.method}</h4>
                                  <ul>
                                    {ci.step.map((step: any, index: any) => (
                                      <li key={`step${index}`}>{step}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {ci.value && (
                                <Grid>
                                  <Cell md={6}>
                                    <b>{translations.product?.specifications && translations.product?.specifications[ci.key.toLowerCase()] ?
                                      translations.product.specifications[ci.key.toLowerCase()] :
                                      ci.key}
                                    </b>
                                  </Cell>
                                  <Cell md={6} gap={1}>
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: ci.value.replace(/^(.*?)\:+/gi, '<b>$1</b>:')
                                      }}
                                    />
                                  </Cell>
                                </Grid>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                </Cell>

                <Cell md={6}>
                  {product.information_panels?.find((ip: any) => ip.shipping_information)?.shipping_information &&
                    (product.information_panels?.find((ip: any) => ip.shipping_information)?.shipping_information.items
                      ?.length ?? 0) > 0 && (
                      <div>
                        <h3>{translations.product?.shipping_information}</h3>
                        {product.information_panels
                          ?.find((ip: any) => ip.shipping_information)
                          ?.shipping_information?.items.map((ci: any) => (
                            <div style={{ marginBottom: 8 }}
                                 key={ci.key}>
                              {ci.step && (
                                <div key={ci._metadata.uid}>
                                  <h4>{ci.method}</h4>
                                  <ul>
                                    {ci.step.map((step: any, index: any) => (
                                      <li key={`step${index}`}>{step}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {ci.value && (
                                <Grid>
                                  <Cell md={6}>
                                    <b>{translations.product?.shipping && translations.product?.shipping[ci.key.toLowerCase()] ?
                                      translations.product.shipping[ci.key.toLowerCase()] :
                                      ci.key}
                                    </b>
                                  </Cell>
                                  <Cell md={6} gap={1}>
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: ci.value.replace(/^(.*?)\:+/gi, '<b>$1</b>:')
                                      }}
                                    />
                                  </Cell>
                                </Grid>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                </Cell>
              </Grid>

              <Grid>
                <Cell md={6}>
                  {product.information_panels?.find((ip: any) => ip.ingredients)?.ingredients &&
                    (product.information_panels?.find((ip: any) => ip.ingredients)?.ingredients.items?.length ?? 0) > 0 && (
                      <div>
                        <h3>{translations.product?.ingredients}</h3>
                        {product.information_panels
                          ?.find((ip: any) => ip.ingredients)
                          ?.ingredients?.items.map((ci: any) => (
                            <div style={{ marginBottom: 8 }}
                                 key={ci.key}>
                              {ci.step && (
                                <div key={ci._metadata.uid}>
                                  <h4>{ci.method}</h4>
                                  <ul>
                                    {ci.step.map((step: any, index: any) => (
                                      <li key={`step${index}`}>{step}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {ci.value && <div dangerouslySetInnerHTML={{ __html: processBoldContains(ci.value, router?.locale) }} />}
                            </div>
                          ))}
                      </div>
                    )}
                </Cell>

                <Cell md={6}>
                  {product.information_panels?.find((ip: any) => ip.allergens)?.allergens &&
                    (product.information_panels?.find((ip: any) => ip.allergens)?.allergens.items?.length ?? 0) > 0 && (
                      <div>
                        <h3>{translations.product?.allergens}</h3>
                        {product.information_panels
                          ?.find((ip: any) => ip.allergens)
                          ?.allergens?.items.map((ci: any) => (
                            <div style={{ marginBottom: 8 }}
                                 key={ci.key}>
                              {ci.step && (
                                <div key={ci._metadata.uid}>
                                  <h4>{ci.method}</h4>
                                  <ul>
                                    {ci.step.map((step: any, index: any) => (
                                      <li key={`step${index}`}>{step}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {ci.value && (
                                <Grid>
                                  <Cell md={6}>
                                    <b>{ci.key}</b>
                                  </Cell>
                                  <Cell md={6} gap={1}>
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: ci.value.replace(/^(.*?)\:+/gi, '<b>$1</b>:')
                                      }}
                                    />
                                  </Cell>
                                </Grid>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                </Cell>
              </Grid>

              {product.information_panels?.find((ip: any) => ip.cooking_instructions)?.cooking_instructions &&
                (product.information_panels?.find((ip: any) => ip.cooking_instructions)?.cooking_instructions.items
                  ?.length ?? 0) > 0 && (
                  <div>
                    <h3>{translations.product?.cooking_instructions}</h3>
                    {product.information_panels
                      ?.find((ip: any) => ip.cooking_instructions)
                      ?.cooking_instructions?.items.map((ci: any) => (
                        <div style={{ marginBottom: 16 }}
                             key={ci.key}>
                          {ci.step && (
                            <div key={ci._metadata.uid}>
                              <h4>{ci.method}</h4>
                              <ul>
                                {ci.step.map((step: any, index: any) => (
                                  <li key={`step${index}`}>{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {ci.value && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: ci.value.replace(/^(.*?)\:+/gi, '<b>$1</b>:')
                              }}
                            />
                          )}
                        </div>
                      ))}
                  </div>
                )}
            </Cell>
            <Cell md={4}>
              {product.information_panels?.map((ip: any) => {
                if (ip.nutritional_information)
                  return (
                    <NutritionalInformation key={ip.nutritional_information._metadata.uid}>
                      <h3 style={{ marginBottom: 0 }}>{translations.product?.nutritional_information}</h3>

                      {router?.locale === 'en-US' ?
                        <Table cellSpacing={0}>
                          <tbody>
                            <Row>
                              <td
                                colSpan={4}
                                style={{
                                  padding: '8px 0',
                                  textAlign: 'left',
                                  borderBottom: '6px #000 solid'
                                }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                  }}>
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 900,
                                      fontFamily: '"GothamBold", "Open Sans", sans-serif'
                                    }}>
                                    {translations.product.serving_size}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      fontFamily: '"GothamBold", "Open Sans", sans-serif'
                                    }}>
                                    {ip.nutritional_information.serving_size}
                                  </span>
                                </div>
                              </td>
                            </Row>
                            <Row>
                              <th
                                colSpan={2}
                                style={{
                                  padding: '8px 0',
                                  textAlign: 'left',
                                  borderBottom: '2px #000 solid'
                                }}>
                                {translations.product.amount}
                              </th>
                              <th
                                colSpan={2}
                                style={{
                                  padding: '8px 0',
                                  textAlign: 'right',
                                  borderBottom: '2px #000 solid'
                                }}>
                                {translations.product.daily_value}
                              </th>
                            </Row>
                            {ip.nutritional_information.items.map((ni: any) =>
                              ni.item === 'Calories' ? (
                                <Row sub2={ni.sub} key={ni._metadata.uid}>
                                  <td
                                    colSpan={2}
                                    style={{
                                      width: '60%',
                                      fontSize: 22,
                                      borderBottom: '1px #000 solid'
                                    }}>
                                    <b>{ni.item}</b>
                                  </td>
                                  <td
                                    colSpan={2}
                                    style={{
                                      textAlign: 'right',
                                      fontSize: 22,
                                      borderBottom: '1px #000 solid'
                                    }}>
                                    <b>{ni.amount.replace(/\D0$/, '')}</b>
                                  </td>
                                </Row>
                              ) : (
                                <Row sub2={ni.sub} key={ni._metadata.uid}>
                                  {ni.sub && <td style={{ width: 15 }}></td>}
                                  <td
                                    colSpan={ni.sub ? 0 : 2}
                                    style={{
                                      width: 180,
                                      borderBottom: `${ni.item.indexOf('Protein') > -1 ? 6 : 1}px #000 solid`
                                    }}>
                                    {ni.sub ? <span>{ni.item}</span> : <b>{ni.item}</b>}
                                  </td>
                                  <td
                                    style={{
                                      width: '20%',
                                      borderBottom: `${ni.item.indexOf('Protein') > -1 ? 6 : 1}px #000 solid`
                                    }}>
                                    {ni.amount.replace(/(\.[0-9]*?)0+/g, '')}
                                  </td>
                                  <td
                                    style={{
                                      width: '20%',
                                      textAlign: 'right',
                                      borderBottom: `${ni.item.indexOf('Protein') > -1 ? 6 : 1}px #000 solid`
                                    }}>
                                    {ni.daily_value.replace(/(\.[0-9]*?)0+/g, '')}
                                  </td>
                                </Row>
                              )
                            )}
                          </tbody>
                        </Table>
                        :
                        <Table cellSpacing={0} $alt={true}>
                          <tbody>
                            <Row>
                              <td
                                colSpan={4}
                                style={{
                                  padding: '8px 0',
                                  textAlign: 'left',
                                  borderBottom: '6px #000 solid'
                                }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                  }}>
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 900,
                                      fontFamily: '"GothamBold", "Open Sans", sans-serif'
                                    }}>
                                    {translations.product.serving_size}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      fontFamily: '"GothamBold", "Open Sans", sans-serif'
                                    }}>
                                    {ip.nutritional_information.serving_size}
                                  </span>
                                </div>
                              </td>
                            </Row>
                            <Row>
                              <th
                                colSpan={2}
                                style={{
                                  padding: '8px 0',
                                  textAlign: 'left',
                                  borderBottom: '1px #000 solid'
                                }}>
                                {translations.product.amount}
                              </th>
                              <th
                                colSpan={2}
                                style={{
                                  padding: '8px 0',
                                  textAlign: 'right',
                                  borderBottom: '1px #000 solid'
                                }}>
                                {translations.product.daily_value}
                              </th>
                            </Row>
                            {ip.nutritional_information.items.map((ni: any) =>
                              <Row sub2={ni.sub} key={ni._metadata.uid} style={{position: 'relative'}}>
                                {ni.sub && <td style={{ width: 15 }}></td>}
                                <td
                                  colSpan={ni.sub ? 0 : 2}
                                  style={{
                                    width: 180,
                                  }}>
                                  {ni.sub || isCDNcompatible(ni.item) ? <span>{ni.item}</span> : <b>{ni.item}</b>}
                                </td>
                                <td
                                  style={{
                                    width: '20%',
                                  }}>
                                  {ni.amount.replace(/(\.[0-9]*?)0+/g, '')}
                                </td>
                                <td
                                  style={{
                                    width: '20%',
                                    textAlign: 'right',
                                    position: 'relative',
                                    top: ni._metadata.uid === 'saturated_fat' ? '18px' : 0,
                                  }}>
                                  {ni.daily_value.replace(/(\.[0-9]*?)0+/g, '')}
                                </td>
                              </Row>
                            )}
                          </tbody>
                        </Table>
                      }
                    </NutritionalInformation>
                  )
              })}
            </Cell>
          </Grid>
        </DetailsContainer>
      )}
      {product.featured_recipe?.length > 0 && (
        <FeaturedRecipe recipe={product.featured_recipe[0]} translations={translations} />
      )}
      {product.page_components?.map((entry: any, i: number) => {
        return (
          <Render
            key={entry.uid}
            pageComponents={entry.page_components?.components}
            contentTypeUid="page"
            translations={translations}
            entryUid={product.uid}
            locale={product.locale}
            product_data={{ product: product, ref: ref }}
          />
        )
      })}
    </Wrapper>
  )
}
export default Product

export const Wrapper = styled.div`
  padding: 0 1.5rem;
  @media ${breakpoint.desktop} {
    padding: 0;
  }

  b,
  th,
  h2,
  h3,
  h4 {
    font-family: 'GothamBold', 'Open Sans', sans-serif;
  }

  ${Container} {
    ${Title} {
      margin-top: 0;
    }
  }
`

const SwiperContainer = styled.div<any>`
  .swiper-container {
    @media ${breakpoint.desktop} {
      display: none;
    }
  }
`

export const Button = styled.a<any>`
  background: var(--primary);
  color: var(--background);
  font-weight: bold;
  font-size: 16px;
  padding: 10px 20px;
  text-transform: uppercase;
  cursor: pointer;
  text-decoration: none;
  border: 1.5px #000 solid;
  border-radius: 2.5px;
  font-family: GothamMedium, 'Open Sans', sans-serif;
  ${(props) =>
    props.light &&
    css`
      color: #000;
      background: #fff;
      margin-left: 30px;
    `}
`

const ProductBtnContainer = styled.div`
  display: flex;
  
  @media ${breakpoint.tablet_down} {
    flex-wrap: wrap;
    gap: 1.2rem;
    justify-content: center;
    
    ${Button} {
      width: 100%;
      margin-left: 0;
      text-align: center;
    }
  }
`

const NutritionalInformation = styled.div`
  border: 1.5px #000 solid;
  background: white;
  padding: 20px;
  @media ${breakpoint.desktop} {
    margin-left: 40px;
  }
  &&& {
    h3 {
      padding-bottom: 8px;
    }
  }
`

const PopoverContent = styled.div`
  background: white;
  box-shadow: 0px 4px 40px rgba(153, 153, 153, 0.4);
  padding: 1rem;
  max-width: 240px;
  a {
    color: black;
  }
`

const DetailsContainer = styled.div`
  background: #f4f0ee;
  & > ${Grid} {
    margin: 0 auto;
    max-width: 1120px;
    padding: 100px 0;
    h3 {
      margin-top: 0;
      margin-bottom: 32px;
      padding-bottom: 15px;
      border-bottom: 1.5px #000 solid;
    }
  }

  & > ${Grid} > ${Cell} > ${Grid} > ${Cell} {
    padding: 24px;
    @media ${breakpoint.desktop} {
      padding: 0 32px 32px 0;
    }
    &:last-child {
      padding-right: 0;
    }
  }
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px;
  flex-direction: column;
  &&& {
    order: 6;
    @media ${breakpoint.desktop} {
      flex-direction: row;
      order: inherit;
    }
  }
`

const IconHighlight = styled.div`
  margin-right: 25px;
  margin-top: 25px;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  position: relative;
`
const Info = styled.div`
  position: absolute;
  color: var(--primary);
  right: 12px;
  top: 5px;
  font-weight: bold;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  line-height: 1;
  font-size: 15px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const Icon = styled.div`
  margin-bottom: 10px;
  display: flex;
  width: 72px;
  height: 72px;
  background: #eaeaea;
  border-radius: 100%;
  align-items: center;
  justify-content: center;
  font-size: 27px;
  color: var(--primary);
  font-weight: 600;
`

const Text = styled.div`
  font-size: 10px;
  text-align: center;
  max-width: 96px;
  color: var(--text);
`

const ProductInfo = styled<any>(Cell)`
  display: flex;
  flex-direction: column;
`

const ThumbnailContainer = styled(Cell)`
  display: none;
  @media ${breakpoint.desktop} {
    display: inherit;
    flex-direction: column;
  }
`

export const Thumbnail = styled.div<any>`
  margin-bottom: 15px;
  margin-right: 15px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  ${(props) =>
    props.background &&
    css`
      background: ${props.background};
    `}
  ${(props) =>
    props.selected &&
    css`
      &:after {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        border: 3px var(--primary) solid;
        box-sizing: border-box;
        top: 0;
        left: 0;
      }
    `}
`

export const ImageContainer = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.shot &&
    css`
      padding: 4rem 2rem;
    `}
  margin-bottom: 2rem;
  position: relative;
  min-height: 460px;
  ${(props) =>
    props.background &&
    css`
      background: ${props.background};
    `}
  img {
    min-height: 460px;
    ${(props) =>
      props.shot &&
      css`
        min-height: 0;
      `}
  }
`

export const Preview = styled.div<any>`
  width: 100%;
  min-height: 460px;
  @media ${breakpoint.desktop} {
    width: 450px;
    height: 460px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.background &&
    css`
      background: ${props.background};
    `}
  ${(props) =>
    props.shot &&
    css`
      padding: 1rem;
    `}
  img {
    ${(props) =>
      props.shot &&
      css`
        width: var(--preview-size);
        height: var(--preview-size);
        max-height: 100%;
        object-fit: contain;
      `}
  }
`

export const Description = styled.div<any>`
  color: var(--text);
  margin: 16px 0;
  font-size: 16px;
  text-align: center;
  order: 0;
  &:nth-child(2) {
    margin-top: 3rem;
  }
  @media ${breakpoint.desktop} {
    text-align: left;
    &:first-child,
    &:nth-child(2) {
      margin-top: 0;
    }
  }
`

const InformationPanels = styled.div`
  order: 2;
  @media ${breakpoint.desktop} {
    order: inherit;
  }
`

const Table = styled.table<any>`
  width: 100%;
  font-size: ${(props) => (props.$alt ? '12px' : '14px')};
  line-height: 16px;
  tr:last-child td {
    border-bottom-width: 4px !important;
  }
`

const Row = styled.tr<any>`
  th,
  td {
    padding: 8px 0;
  }
  ${(props) =>
    props.sub &&
    css`
      td {
        padding: 2px 0;
        position: relative;
        top: -8px;
      }
    `}

  ${(props) =>
    props.sub2 &&
    css`
      td {
        padding: 10px 0;
        font-size: 12px;
      }
    `}
`

export const Reviews = styled.div<any>`
  min-height: 44px;
  order: 1;
  @media screen and ${breakpoint.desktop} {
    order: inherit;
  }
  .yotpo {
    &-bottomline {
      display: flex;
      align-items: center;
      justify-content: center;
      @media ${breakpoint.desktop} {
        justify-content: flex-start;
      }
      
      a {
        color: var(--primary) !important;
        &:hover {
          color: var(--primary) !important;
        }
      }
    }
    
    &-stars {
      margin-right: 1.75rem;
    }
    
    &-icon {
      color: var(--primary) !important;
      font-size: 1.25rem !important;
    }
  }
`
